import { Avatar } from '../../../ui/primitives/Avatar.tsx';
import { Badge } from '../../../ui/primitives/Badge.tsx';
import { Button } from '../../../ui/primitives/Button.tsx';
import { Text } from '../../../ui/primitives/Text.tsx';
import { BlockButton } from '../BlockButton.tsx';
import { FollowButton } from '../FollowButton.tsx';
import { FollowLists } from '../FollowLists.tsx';
import { InviteLink } from '../InviteLink.tsx';
import { Counted } from '../shared/Counted.tsx';
import { WeekStrip } from '../shared/WeekStrip.tsx';
import type { ProfileDirectionProps } from '../shared/types.ts';
import styles from './Editorial.module.css';

/**
 * Direction 4 — Editorial.
 *
 * THE ONE DIRECTION WITH NO HERO SURFACE AT ALL. § 8 caps a screen at one L3
 * and never requires it, and every other Profile composition here opens on the
 * same floating gradient card — so the honest way to find out whether that card
 * is carrying the screen is to take it away and see what is left. What is left
 * is a masthead: the person's name, and under it the one number that never
 * falls, set at a size no surface has to announce.
 *
 * THE SCREEN'S ONE GRADIENT PHRASE (§ 5.3) IS SPENT ON THE TOTAL. The rule is
 * "a fragment, not a block — one phrase per screen, inside a line that is
 * otherwise `foreground`", which is why the numerals take `grad/text` and the
 * word "pontos" beside them does not. Nothing else on this page is gradient:
 * not the name, not the figures, not a rule or a border. That is the entire
 * budget, spent once, on the fact the screen exists to state.
 *
 * NO DECORATIVE RULES. § 6 binds `grad/border` to "a sign of present
 * interactivity — never static decoration", so the structure here is made of
 * type size and empty space, with a hairline only between the figures in the
 * spec row, which is § 8's one licence for a border: separation a surface is
 * not there to carry.
 *
 * AT 1024 THE PAGE BECOMES TWO COLUMNS AND THE IDENTITY STOPS SCROLLING
 * (Editorial.module.css). Who you are is a caption to the whole page here, not
 * a banner across the top of it.
 */
export function Editorial({
  profile,
  weekDays,
  followFixture,
  onFollowChange,
  onOpenSettings,
}: ProfileDirectionProps) {
  const { learner, points, follows, streak } = profile;

  return (
    <div className={styles.page}>
      <header className={styles.rail}>
        <Avatar name={learner.name} image={learner.image} size="lg" />

        <Text as="h1" variant="display-lg">
          {learner.name}
        </Text>

        <div className={styles.relation}>
          <Text as="span" variant="label" tone="muted">
            {follows.following} seguindo · {follows.followers}{' '}
            {follows.followers === 1 ? 'seguidor' : 'seguidores'}
          </Text>
          {!profile.you && profile.followsYou ? <Badge tone="accent">segue você</Badge> : null}
        </div>

        <div className={styles.actions}>
          {profile.you ? (
            <Button variant="secondary" onClick={onOpenSettings}>
              Configurações da conta
            </Button>
          ) : (
            <>
              <FollowButton userId={learner.id} following={profile.following} onChange={onFollowChange} />
              <BlockButton userId={learner.id} name={learner.name} />
            </>
          )}
        </div>
      </header>

      <div className={styles.body}>
        {/* The masthead figure. The numerals carry § 5.3's gradient; the unit
            beside them stays `foreground`, which is what keeps this a fragment
            rather than a second hero. */}
        <p className={styles.headline}>
          <Counted value={points.lifetime} variant="display-xl" tone="gradient" />
          <Text as="span" variant="subheading">
            pontos desde sempre
          </Text>
        </p>

        <dl className={styles.spec} aria-label="O que esta pessoa acumulou">
          <Spec value={streak.current} unit={streak.current === 1 ? 'dia seguido' : 'dias seguidos'} />
          <Spec value={streak.longest} unit="recorde" />
          <Spec value={profile.podiums} unit={profile.podiums === 1 ? 'pódio' : 'pódios'} />
          <Spec value={points.week} unit="nesta semana" />
        </dl>

        {profile.you ? (
          <section className={styles.rhythm} aria-label="Seus últimos sete dias">
            <WeekStrip days={weekDays} />
            <Text as="p" variant="body">
              {streak.current === 0
                ? 'Pratique hoje para começar uma sequência.'
                : streak.practisedToday
                  ? 'Você praticou hoje.'
                  : 'Hoje ainda dá tempo.'}
            </Text>
          </section>
        ) : null}

        {profile.you ? (
          <>
            <InviteLink userId={learner.id} />
            <FollowLists fixture={followFixture} />
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * One figure in the spec row: the numeral over its unit, in the compact
 * `token-lg` step rather than `numeric-display`, because four of them in a row
 * are a line of data and not four headlines.
 */
function Spec({ value, unit }: { value: number; unit: string }) {
  return (
    <div className={styles.specItem}>
      <dt className={styles.specValue}>
        <Counted value={value} variant="token-lg" />
      </dt>
      <dd className={styles.specUnit}>
        <Text as="span" variant="label" tone="muted">
          {unit}
        </Text>
      </dd>
    </div>
  );
}
